import { authRegister, readOptionsAuthRegister } from '@/redux/actions';
import * as Constants from '@/redux/constants';
import { faArrowRightToBracket } from '@fortawesome/free-solid-svg-icons';
import { Divider, Stack, Typography } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid2';
import { useDialogs } from '@toolpad/core';
import icon from 'assets/login-page-icon.png';
import { Button } from 'components/button';
import { LanguageSwitcher } from 'components/language-switcher';
import { useDispatch, useLocale, useSelector } from 'hooks';
import { PasswordInput, passwordStrengthCriteria, TextInput } from 'components/input';
import { convertEmptyStringToNull } from 'lib/utils';
import { useI18n } from 'locales';
import get from 'lodash/get';
import { AuthRegister } from 'models';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { shallowEqual } from 'react-redux';
import ServiceSelectBox from './components/ServiceSelectBox';

const selector = (state) => ({
  authRegisterInfo: state.authRegisterInfo,
  optionsAuthRegisterInfo: state.optionsAuthRegisterInfo,
});

function CreateAccountPage() {
  const dispatch = useDispatch();
  const locale = useLocale();
  const { authRegisterInfo, optionsAuthRegisterInfo } = useSelector(selector, shallowEqual);
  const { control, formState: { errors }, handleSubmit, watch, setError } = useForm();
  const dialogs = useDialogs();
  const t = useI18n();
  const isFetching = authRegisterInfo.isFetching;
  const serviceOptions = get(optionsAuthRegisterInfo, 'data.serviceId', []);
  const watchedServiceId = watch('serviceId');
  const watchedEmail = watch('email');
  const watchedPassword = watch('password');
  const isPasswordStrong = passwordStrengthCriteria.every((test) => test(watchedPassword || ''));

  const fields = useMemo(() => {
    const ret = [];
    let rows = AuthRegister.form.create;

    for (const row of rows) {
      // if row is string
      if (typeof row === 'string') {
        ret.push(AuthRegister.fields[row]);
      } else {
        const tmp = [];
        for (const field of row) {
          tmp.push(AuthRegister.fields[field]);
        }
        ret.push(tmp);
      }
    }

    return ret;
  }, []);

  useEffect(() => {
    dispatch(readOptionsAuthRegister());
  }, [dispatch]);

  useEffect(() => {
    switch (authRegisterInfo.type) {
      case Constants.AUTH_REGISTER.SUCCESS:
        location.href = `/giving/register/verify?email=${authRegisterInfo.data.email}&locale=${locale}`;
        break;
      case Constants.AUTH_REGISTER.FAILURE: {
        let isPendingVerification = false;
        for (const entry of Object.entries(get(authRegisterInfo, 'error.errors', {}))) {
          for (const message of entry[1]) {
            setError(entry[0], { type: 'manual', message });

            if (entry[0] === 'email' && message.indexOf('not verified') !== -1) {
              isPendingVerification = true;
            }
          }
        }

        if (isPendingVerification) {
          dialogs.alert(
            t('Your email is registered but not verified. Please check your email for verification instructions.'),
            {
              title: t('Email Not Verified'),
              okText: t('OK'),
              onClose: () => {
                location.href = `/giving/register/verify?email=${watchedEmail}&locale=${locale}`;
              },
            }
          );
        }
      }
        break;
    }
  }, [authRegisterInfo, locale, setError, watchedEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = useCallback((data) => {
    if (!passwordStrengthCriteria.every((test) => test(data.password || ''))) {
      dialogs.alert(t('Password does not meet strength requirements. Please check the checklist and try again.'), {
        title: t('Weak Password'),
        okText: t('OK'),
      });
      return;
    }

    data = convertEmptyStringToNull(data);

    for (let row of fields) {
      if (!Array.isArray(row)) {
        row = [row];
      }
    }

    dispatch(authRegister({ payload: data }));
  }, [dispatch, dialogs, fields, t]);

  const onError = useCallback((/* errors */) => {
    dialogs.alert(t('There are errors in the form. Please check the form and try again.'), { title: t('Error'), okText: t('OK') });
  }, [dialogs, t]);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit(onSubmit, onError)}
      gap={4}
      sx={{
        backgroundColor: 'background.default',
        alignItems: 'center',
        justifyContent: 'center',
        py: 8,
      }}
    >
      <Stack direction="row" justifyContent="flex-end" sx={{ width: '100%', mt: -2, px: 2 }}>
        <LanguageSwitcher />
      </Stack>

      <Stack gap={2} sx={{ alignItems: 'center', display: 'flex', width: isMobile ? '80%' : '280px' }}>
        <img src={icon} alt='icon' style={{ width: '100px', height: '100px' }} />

        <Typography variant='h5' color='secondary.main' sx={{ textAlign: 'center', fontWeight: 'bold' }}>
          {t('GIVING')}
        </Typography>
      </Stack>

      <Stack sx={{ width: isMobile ? '95%' : '500px', flexDirection: 'column', display: 'flex', alignItems: 'center' }}>
        <Typography variant='h2' color='secondary.main' sx={{ p: 2, fontWeight: 'bold' }}>
          {t('CREATE ACCOUNT')}
        </Typography>
        <Divider sx={{ width: '95%', height: '4px', backgroundColor: 'primary.main' }} />
      </Stack>


      <Stack gap={2} sx={{ alignItems: 'center', display: 'flex', width: isMobile ? '90%' : '500px' }}>
        {fields.map((row, index) => {
          const rowFields = Array.isArray(row) ? row : [row];

          return (
            <Stack
              key={index}
              direction={rowFields.length > 1 ? 'row' : 'column'}
              gap={2}
              sx={{ width: '100%' }}
            >
              {rowFields.map((field) => {
                if (field.name === 'serviceId') {
                  const error = get(errors, 'serviceId');
                  return (
                    <Controller
                      key={field.name}
                      name={field.name}
                      control={control}
                      rules={AuthRegister.validationRules[field.name]}
                      render={({ field: { onChange } }) => (
                        <Stack gap={1} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
                          <Typography variant='h6' color='secondary.main' sx={{ fontWeight: 'bold' }}>
                            {t('Which Is Your Regular Service?')}
                          </Typography>

                          <FormControl
                            variant='filled'
                            error={!!error}
                            fullWidth
                          >
                            <Grid container gap={1} direction="row" sx={{ alignItems: 'center', justifyContent: 'center' }}>
                              {
                                serviceOptions?.map((service, index) => (
                                  <ServiceSelectBox
                                    key={`service-select-box-${index}`}
                                    service={service}
                                    selected={service.value === watchedServiceId}
                                    onSelect={() => onChange(service.value)}
                                  />
                                ))
                              }
                            </Grid>
                            <FormHelperText sx={{ textAlign: 'center', pt: 1 }}>{get(error, 'message')}</FormHelperText>
                          </FormControl>
                        </Stack>
                      )}
                    />
                  );
                }

                return (
                  <Controller
                    key={field.name}
                    name={field.name}
                    control={control}
                    rules={AuthRegister.validationRules[field.name]}
                    render={({ field: { onChange, value } }) => {
                      const isPasswordField = field.name === 'password';
                      const isConfirmField = field.name === 'confirmPassword';

                      if (isPasswordField || isConfirmField) {
                        return (
                          <PasswordInput
                            label={t(field.label)}
                            value={value || ''}
                            onChange={onChange}
                            error={!!errors[field.name]}
                            helper={errors[field.name]?.message ? t(errors[field.name].message) : undefined}
                            showChecklist={isPasswordField}
                            maxLength={field.maxLength}
                            fullWidth
                          />
                        );
                      }

                      return (
                        <TextInput
                          label={t(field.label)}
                          type={field.type}
                          value={value || ''}
                          onChange={onChange}
                          error={!!errors[field.name]}
                          helper={t(errors[field.name]?.message ?? field.helperText)}
                          maxLength={field.maxLength}
                          fullWidth
                        />
                      );
                    }}
                  />
                );
              })}
            </Stack>
          );
        })}
      </Stack>

      <Stack sx={{ display: 'flex', width: '300px' }}>
        <Button
          type="submit"
          variant="contained"
          size="large"
          leading={faArrowRightToBracket}
          fullWidth
          disabled={isFetching || !isPasswordStrong}
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            borderRadius: '8px',
            '&:hover': {
              backgroundColor: 'primary.dark',
            }
          }}
        >
          {isFetching ? t('Creating Account...') : t('Create Account')}
        </Button>
      </Stack>
    </Stack>
  );
}

export default CreateAccountPage;
